import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Trash2, Upload, Video } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api, { API } from "@/lib/api";
import { MAX_IMAGES, MAX_IMAGE_MB, MAX_VIDEO_MB, SPECIES, uploadMarketFile } from "@/lib/market";
import { useI18n } from "@/context/I18nContext";

function MediaPicker({ t, existing, pending, onPick, onRemoveExisting, onRemovePending, uploads }) {
  const inputRef = useRef(null);
  const images = existing.filter((m) => m.kind === "image").length + pending.filter((f) => f.type.startsWith("image/")).length;
  const hasVideo = existing.some((m) => m.kind === "video") || pending.some((f) => f.type.startsWith("video/"));
  const pick = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    const accepted = [];
    let imgs = images, vid = hasVideo;
    for (const f of files) {
      if (f.type.startsWith("image/")) {
        if (imgs >= MAX_IMAGES) { toast.error(t("market.err_max_images").replace("{n}", MAX_IMAGES)); continue; }
        if (f.size > MAX_IMAGE_MB * 1024 * 1024) { toast.error(t("market.err_image_size").replace("{n}", MAX_IMAGE_MB)); continue; }
        imgs += 1;
      } else if (f.type.startsWith("video/")) {
        if (vid) { toast.error(t("market.err_one_video")); continue; }
        if (f.size > MAX_VIDEO_MB * 1024 * 1024) { toast.error(t("market.err_video_size").replace("{n}", MAX_VIDEO_MB)); continue; }
        vid = true;
      } else { toast.error(t("market.err_type")); continue; }
      accepted.push(f);
    }
    if (accepted.length) onPick(accepted);
  };
  return (
    <div className="space-y-2" data-testid="media-picker">
      <Label>{t("market.media")}</Label>
      <p className="text-[11px] text-espresso/60">{t("market.media_hint").replace("{n}", MAX_IMAGES).replace("{i}", MAX_IMAGE_MB).replace("{v}", MAX_VIDEO_MB)}</p>
      <input ref={inputRef} type="file" multiple accept="image/*,video/*" className="hidden" onChange={pick} data-testid="media-input" />
      <Button type="button" variant="outline" onClick={() => inputRef.current?.click()} data-testid="media-browse">
        <Upload className="w-4 h-4 mr-1 rtl:mr-0 rtl:ml-1" />{t("market.browse")}
      </Button>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {existing.map((m) => (
          <div key={m.id} className="relative aspect-square rounded-lg overflow-hidden bg-cream-2 border border-app-border" data-testid={`existing-media-${m.id}`}>
            {m.kind === "video" ? <video src={`${API}/market/media/${m.id}`} className="w-full h-full object-cover" muted /> : <img src={`${API}/market/media/${m.id}`} alt="" className="w-full h-full object-cover" />}
            <button type="button" onClick={() => onRemoveExisting(m.id)} className="absolute top-1 right-1 bg-white/90 rounded-full p-1 text-red-700" aria-label="remove"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        {pending.map((f, i) => (
          <div key={i} className="relative aspect-square rounded-lg overflow-hidden bg-cream-2 border border-app-border flex items-center justify-center" data-testid={`pending-media-${i}`}>
            {f.type.startsWith("video/") ? <Video className="w-8 h-8 text-espresso/40" /> : <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover" />}
            {uploads[i] !== undefined && uploads[i] < 1 && (
              <div className="absolute inset-x-0 bottom-0 h-1.5 bg-black/20"><div className="h-full bg-olive" style={{ width: `${Math.round(uploads[i] * 100)}%` }} /></div>
            )}
            {uploads[i] === undefined && (
              <button type="button" onClick={() => onRemovePending(i)} className="absolute top-1 right-1 bg-white/90 rounded-full p-1 text-red-700" aria-label="remove"><Trash2 className="w-3.5 h-3.5" /></button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function MarketFormPage() {
  const { type, id } = useParams();
  const { t } = useI18n();
  const navigate = useNavigate();
  const editing = Boolean(id);
  const [form, setForm] = useState({ type: type || "sell", species: "canary", title: "", description: "" });
  const [existing, setExisting] = useState([]);
  const [removed, setRemoved] = useState([]);
  const [pending, setPending] = useState([]);
  const [uploads, setUploads] = useState({});
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(!editing);

  useEffect(() => {
    if (!editing) return;
    api.get(`/market/listings/${id}`).then(({ data }) => {
      setForm({ type: data.type, species: data.species, title: data.title, description: data.description });
      setExisting(data.media);
      setLoaded(true);
    }).catch(() => navigate("/market"));
  }, [editing, id, navigate]);

  if (!editing && type !== "sell" && type !== "buy") return <Navigate to="/market" replace />;
  if (!loaded) return <Loader2 className="w-5 h-5 animate-spin text-espresso/50" />;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let listingId = id;
      if (editing) {
        await api.patch(`/market/listings/${id}`, { species: form.species, title: form.title, description: form.description });
        for (const mid of removed) await api.delete(`/market/listings/${id}/media/${mid}`);
      } else {
        const { data } = await api.post("/market/listings", form);
        listingId = data.id;
      }
      for (let i = 0; i < pending.length; i++) {
        setUploads((u) => ({ ...u, [i]: 0 }));
        await uploadMarketFile(listingId, pending[i], (p) => setUploads((u) => ({ ...u, [i]: p })));
      }
      toast.success(t(editing ? "market.saved" : "market.published"));
      navigate(`/market/listing/${listingId}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("market.err_upload"));
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-4" data-testid="market-form-page">
      <div className="flex items-center gap-3">
        <Link to={editing ? `/market/listing/${id}` : `/market/${type}`} className="text-espresso/60 hover:text-espresso" data-testid="form-back"><ArrowLeft className="w-5 h-5 rtl:rotate-180" /></Link>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-espresso">
          {editing ? t("market.edit_offer") : t("market.add_bird")} · {t(form.type === "sell" ? "market.selling" : "market.buying")}
        </h1>
      </div>
      <form onSubmit={submit} className="bg-white rounded-2xl border border-app-border p-4 sm:p-6 space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="species">{t("market.species")}</Label>
            <select id="species" value={form.species} onChange={set("species")} className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="species-select">
              {SPECIES.map((s) => <option key={s} value={s}>{t(`market.species_${s}`)}</option>)}
            </select>
          </div>
          <div>
            <Label htmlFor="title">{t("market.offer_title")}</Label>
            <Input id="title" value={form.title} onChange={set("title")} required minLength={2} maxLength={120} className="mt-1" data-testid="title-input" />
          </div>
        </div>
        <div>
          <Label htmlFor="desc">{t("market.description")}</Label>
          <Textarea id="desc" rows={6} value={form.description} onChange={set("description")} required minLength={2} maxLength={4000} placeholder={t("market.description_hint")} className="mt-1" data-testid="description-input" />
        </div>
        <MediaPicker
          t={t}
          existing={existing.filter((m) => !removed.includes(m.id))}
          pending={pending}
          uploads={uploads}
          onPick={(files) => setPending([...pending, ...files])}
          onRemoveExisting={(mid) => setRemoved([...removed, mid])}
          onRemovePending={(i) => setPending(pending.filter((_, j) => j !== i))}
        />
        <div className="flex justify-end gap-2">
          <Button type="submit" disabled={busy} data-testid="submit-listing">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1 rtl:mr-0 rtl:ml-1" />}
            {busy ? t("market.uploading") : editing ? t("common.save") : t("market.upload")}
          </Button>
        </div>
      </form>
    </div>
  );
}
